<script setup lang="ts">
import { inject, onMounted, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';
import { reconcileIdvStatusApi } from '@Api/idv.api';
import { IdvNextAction } from '@Enums/idvNextAction.enum';
import { getHostRouter } from '@Src/mfe.config';
import { FlowLayout } from '@Shared';
import type { DashboardContext } from '@Shared';

const router = useRouter();
const currentRoute = useRoute();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const companyImageBaseUrl = useCompanyImageBaseUrl();

// Same provider useDashboardContext reads — tells us whether this match is being
// done inside the participant portal (a deal with more tasks) vs a standalone
// white-label single-request.
const getDashboardContext = inject<() => Promise<DashboardContext>>(
    'backToDashboard:getDashboardContext',
    () => Promise.resolve({ participantPortalEnabled: false, dealId: null }),
);

/**
 * Delay before each re-poll, in order; the last value repeats. Starts tight because the common
 * case settles within a few seconds of the redirect, then eases off so a slow evaluation costs
 * requests rather than a request per second.
 */
const POLL_DELAYS_MS = [1000, 2000, 3000, 5000, 5000, 10000];

/**
 * How long the page keeps waiting for the request to settle. Sized for the vendor's own tail
 * rather than the redirect: a capture that has not resolved in three minutes is not going to.
 */
const MAX_POLL_DURATION_MS = 180000;

/**
 * Unreadable responses tolerated in a row before giving up. One is a blip on a poll this long;
 * three in a row is the session or the network being gone.
 */
const MAX_CONSECUTIVE_FAILURES = 3;

const SUBMITTED_ROUTE = 'match-idv-submitted';

const nextActionRouteMap: Partial<Record<IdvNextAction, string>> = {
    [IdvNextAction.RetryCapture]: 'match-idv-retry',
    [IdvNextAction.ExitFlow]: 'match-idv-ended',
    [IdvNextAction.ShowSubmitted]: SUBMITTED_ROUTE,
};

// PS-955: in the participant portal a successful verification returns the
// participant to the dashboard so they can continue any remaining tasks (this
// restores the pre-PS-955 destination — the dashboard redirect that used to fire
// at mount no longer runs for the Intellicheck capture return). A failed outcome
// still goes to the in-flow retry screen. Outside the portal (standalone
// white-label single-request), success stays on the terminal "submitted" view.
const navigateToOutcome = (routeName: string, inDashboardContext: boolean) => {
    if (routeName === SUBMITTED_ROUTE && inDashboardContext) {
        const hostRouter = getHostRouter();
        if (hostRouter) {
            hostRouter.push({
                name: 'dashboard',
                params: { companySlug: hostRouter.currentRoute.value.params.companySlug },
            });
            return;
        }
    }
    router.push({ name: routeName, params: { requestType: currentRoute.params.requestType } });
};

const resolveRoute = (result: { nextAction: IdvNextAction } | undefined): string | null =>
    result ? (nextActionRouteMap[result.nextAction] ?? null) : null;

/**
 * Polls the IDV status until the request settles, then navigates to the terminal view.
 *
 * Every call is `reconcileIdvStatusApi`: this page is only reached once the capture finished, so
 * a request still sitting at Started means the result has not landed yet. The BFF POST pulls the
 * result from the vendor and settles the status server-side, and it is idempotent — the guard on
 * the BFF makes a redelivery a no-op — so calling it repeatedly is safe.
 *
 * `ContinuePolling` means the vendor has not finished, not that the capture failed. The vendor
 * finalises its evaluation a few seconds after the redirect fires, so a page that gave up after
 * two quick attempts routed people to the retry screen while their passing result was still in
 * flight — and every such retry burned one of their attempts. So we keep waiting, backing off,
 * for as long as a capture could plausibly still be resolving.
 *
 * Only a request that is still unsettled when that budget runs out falls back to retry. A run of
 * consecutive unreadable responses (network failure, missing session) routes to the general error
 * page, but a single blip does not — over a long poll, one is expected.
 */
let unmounted = false;

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const delayForAttempt = (attempt: number) =>
    POLL_DELAYS_MS[Math.min(attempt, POLL_DELAYS_MS.length - 1)];

onMounted(async () => {
    // Started before the status call rather than awaited ahead of it: the two are
    // independent, and PS-1169 means a portal session now resolves this over the network
    // instead of short-circuiting. Defaults to non-dashboard on any failure (mirrors
    // useDashboardContext) so a context lookup hiccup never strands the participant.
    const dashboardContextPromise = getDashboardContext()
        .catch(() => ({ participantPortalEnabled: false, dealId: null }));

    // Awaited only once there is somewhere to navigate, so the lookup never delays the first
    // status call — the point of starting it as a promise above.
    const inDashboardContext = async () =>
        (await dashboardContextPromise)?.participantPortalEnabled === true;

    const startedAt = Date.now();
    let attempt = 0;
    let consecutiveFailures = 0;

    try {
        while (!unmounted) {
            const idvStatusResult = await reconcileIdvStatusApi();
            if (unmounted) return;

            if (idvStatusResult) {
                consecutiveFailures = 0;

                const route = resolveRoute(idvStatusResult);
                if (route) {
                    navigateToOutcome(route, await inDashboardContext());
                    return;
                }
            } else if (++consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
                console.error(`No IDV status result after ${consecutiveFailures} consecutive attempts`);
                router.push({ name: 'general-error', params: { requestType: currentRoute.params.requestType } });
                return;
            }

            if (Date.now() - startedAt >= MAX_POLL_DURATION_MS) {
                break;
            }

            await sleep(delayForAttempt(attempt++));
        }

        if (unmounted) return;

        // Still unsettled after the full budget. Retry is the honest answer now: something did
        // not land, and the recipient needs a way forward.
        console.warn('IDV status did not settle within the polling budget; routing to retry');
        navigateToOutcome('match-idv-retry', await inDashboardContext());
    } catch (error) {
        console.error('Failed to get IDV status', error);
        router.push({ name: 'general-error', params: { requestType: currentRoute.params.requestType } });
    }
});

onUnmounted(() => {
    unmounted = true;
});
</script>

<template>
    <flow-layout
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        :show-back-button="false">

        <app-form-content-layout>
            <div class="verification-processing-complete">
                <app-brand-loader>
                    Processing your verification...
                    <template #caption>
                        <p>This may take a few seconds, do not refresh this page.</p>
                    </template>
                </app-brand-loader>
            </div>
        </app-form-content-layout>
    </flow-layout>
</template>

<style lang="scss" scoped>
.verification-processing-complete {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
}
</style>
